import type { ShopCheckoutRequestItem } from "@shared/shop.utils";
import { FunctionsHttpError } from "@supabase/supabase-js";
import type { Tables } from "../../database.types";
import { supabaseClient } from "../../shared/supabase";
import {
  mapShopProductDetail,
  type ShopProductDetail,
  type ShopProductDetailRecord,
} from "./catalogue";

/**
 * The columns every product detail view needs. The detail page and the cart
 * both feed the same `mapShopProductDetail`, so they share one column list
 * instead of keeping copies in step by hand.
 */
const SHOP_PRODUCT_DETAIL_SELECT = `
  id,
  name,
  description,
  main_image_url,
  gallery_image_urls,
  price_cents,
  deleted_at,
  display_order,
  created_at,
  shop_product_variants (
    id,
    label,
    available_quantity,
    sort_order,
    deleted_at
  )
`;

/**
 * Stock and prices change rarely compared with how often a buyer clicks, and
 * the same product is usually wanted twice in a few seconds: the detail page
 * shows it, then the cart takes it. A short window is long enough to spare the
 * second query and short enough that availability stays honest.
 */
export const SHOP_PRODUCT_CACHE_TTL_MS = 30 * 1000;

// The promise is cached, not just its result, so calls that overlap share one
// query instead of racing each other.
const productCache = new Map<
  string,
  { expiresAt: number; product: Promise<ShopProductDetail | null> }
>();

const readCachedProduct = (productId: string, now: number) => {
  const entry = productCache.get(productId);

  if (!entry) {
    return null;
  }

  if (entry.expiresAt <= now) {
    productCache.delete(productId);
    return null;
  }

  return entry.product;
};

const cacheProduct = (
  productId: string,
  product: Promise<ShopProductDetail | null>,
  now: number,
) => {
  productCache.set(productId, {
    expiresAt: now + SHOP_PRODUCT_CACHE_TTL_MS,
    product,
  });

  return product;
};

/**
 * Empties the cache. For tests, and for anything that knows the catalogue
 * changed under it.
 */
export const clearShopProductCache = () => {
  productCache.clear();
};

const requestShopProductById = async (
  productId: string,
): Promise<ShopProductDetail | null> => {
  const { data, error } = await supabaseClient
    .from("shop_products")
    .select(SHOP_PRODUCT_DETAIL_SELECT)
    .eq("id", productId)
    .is("deleted_at", null)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return mapShopProductDetail(data as ShopProductDetailRecord | null);
};

/**
 * Loads one product still on sale, or null when the id does not resolve.
 * Served from the short-lived cache when it was asked for recently.
 */
export const fetchShopProductById = (
  productId: string,
): Promise<ShopProductDetail | null> => {
  const now = Date.now();
  const cached = readCachedProduct(productId, now);

  if (cached) {
    return cached;
  }

  const product = requestShopProductById(productId).catch((error) => {
    // A failed query must not be remembered as an answer.
    productCache.delete(productId);
    throw error;
  });

  return cacheProduct(productId, product, now);
};

/**
 * Loads the products still on sale among the given ids. Ids that no longer
 * resolve are simply absent from the result: callers decide what that means
 * for them.
 */
export const fetchShopProductsByIds = async (
  productIds: string[],
): Promise<ShopProductDetail[]> => {
  if (productIds.length === 0) {
    return [];
  }

  const { data, error } = await supabaseClient
    .from("shop_products")
    .select(SHOP_PRODUCT_DETAIL_SELECT)
    .in("id", productIds)
    .is("deleted_at", null);

  if (error) {
    throw error;
  }

  const products = (data as ShopProductDetailRecord[])
    .map((record) => {
      return mapShopProductDetail(record);
    })
    .filter((product): product is ShopProductDetail => {
      return product !== null;
    });

  // Seed the cache so an add that follows a cart restore costs no query.
  const now = Date.now();

  products.forEach((product) => {
    cacheProduct(product.id, Promise.resolve(product), now);
  });

  return products;
};

/**
 * Asks the server for a Stripe embedded checkout for the given cart. Only the
 * buyer's own choices travel: the server reads names, prices and availability
 * back from the catalogue and refuses a cart it cannot charge.
 *
 * A refusal comes back as an `Error` carrying the server's own message, so the
 * cart can tell the buyer exactly what to fix.
 */
export const startShopCheckout = async (
  items: ShopCheckoutRequestItem[],
  note: string,
): Promise<string> => {
  const trimmedNote = note.trim();

  const { data, error } = await supabaseClient.functions.invoke(
    "server/checkout_shop",
    {
      method: "POST",
      body: {
        items,
        ...(trimmedNote.length > 0 ? { note: trimmedNote } : {}),
      },
    },
  );

  if (error) {
    throw new Error(
      await readFunctionErrorMessage(
        error,
        "Non è stato possibile avviare il pagamento",
      ),
    );
  }

  if (!data?.clientSecret) {
    throw new Error("Non è stato possibile avviare il pagamento");
  }

  return data.clientSecret as string;
};

/**
 * Asks the server for the Stripe status of a checkout session: `open` while
 * the buyer has not paid yet, `complete` once Stripe took the money, `expired`
 * when the session can no longer be paid.
 */
export const fetchShopPaymentStatus = async (
  sessionId: string,
): Promise<string> => {
  const fallback = "Non è stato possibile verificare il pagamento";

  const { data, error } = await supabaseClient.functions.invoke(
    `server/payment_status/${sessionId}`,
    { method: "GET" },
  );

  if (error) {
    throw new Error(await readFunctionErrorMessage(error, fallback));
  }

  if (!data?.status) {
    throw new Error(fallback);
  }

  return data.status as string;
};

export type ShopOrderWithItems = Tables<"shop_orders"> & {
  shop_order_items: Tables<"shop_order_items">[];
};

/**
 * Loads the order a checkout session produced, or null while the Stripe
 * webhook has not created it yet. The order is found through its payment,
 * which is the only row that remembers the session.
 */
export const fetchShopOrderBySessionId = async (
  sessionId: string,
): Promise<ShopOrderWithItems | null> => {
  const { data, error } = await supabaseClient
    .from("shop_orders")
    .select("*, shop_order_items(*), payments!inner(stripe_session_id)")
    .eq("payments.stripe_session_id", sessionId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data as ShopOrderWithItems | null;
};

const readFunctionErrorMessage = async (error: unknown, fallback: string) => {
  if (!(error instanceof FunctionsHttpError)) {
    return fallback;
  }

  try {
    const body = await error.context.json();
    return typeof body?.error === "string" ? body.error : fallback;
  } catch {
    return fallback;
  }
};

export type ShopOrderWithPayment = ShopOrderWithItems & {
  payments: Tables<"payments"> | null;
};

/**
 * Row level security lets administrators read every order, so the account
 * filter keeps the account area about the signed in family.
 */
export const fetchShopOrders = async (
  userId: string,
): Promise<ShopOrderWithPayment[]> => {
  const { data, error } = await supabaseClient
    .from("shop_orders")
    .select("*, shop_order_items(*), payments(*)")
    .eq("user_id", userId)
    .is("deleted_at", null)
    .order("created_at", { ascending: false })
    .order("id", { ascending: true });

  if (error) {
    throw error;
  }

  return data as ShopOrderWithPayment[];
};
